import { useEffect, useState } from "react";
import { Button, Field, Spinner, StatusBadge } from "@leylion/design-system";
import {
  addPlatformAdministrator,
  createPlatformSecurityIdempotencyKey,
  getPlatformAdministrators,
  getPlatformAdministratorSessions,
  mutatePlatformAdministrator,
  revokeAllPlatformAdministratorSessions,
  revokePlatformSession,
  type PlatformAdministratorSummary,
  type PlatformSessionSummary
} from "./platform-security-admin-client";
import { PlatformConfirmationDialog } from "./PlatformConfirmationDialog";
import { PlatformDiagnosticAlert } from "./PlatformDiagnosticAlert";
import {
  toPlatformDiagnostic,
  type PlatformDiagnostic,
  type PlatformLanguage
} from "./platform-diagnostics";

type MembershipAction = "suspend" | "reactivate" | "revoke";
type PendingAction =
  | { kind: "grant"; email: string }
  | { kind: "membership"; action: MembershipAction; administrator: PlatformAdministratorSummary }
  | { kind: "session"; administrator: PlatformAdministratorSummary; session: PlatformSessionSummary }
  | { kind: "all-sessions"; administrator: PlatformAdministratorSummary };

const auditRefreshEvent = "leylion-platform-audit-refresh";

function formatTimestamp(value: string, language: PlatformLanguage): string {
  return new Date(value).toLocaleString(language === "ar" ? "ar-AE" : "en-AE", {
    dateStyle: "medium",
    timeStyle: "short"
  });
}

export function PlatformAdministratorPanel({
  token,
  language
}: {
  token: string;
  language: PlatformLanguage;
}) {
  const ar = language === "ar";
  const [items, setItems] = useState<PlatformAdministratorSummary[]>([]);
  const [selected, setSelected] = useState<PlatformAdministratorSummary | null>(null);
  const [sessions, setSessions] = useState<PlatformSessionSummary[]>([]);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(true);
  const [actionBusy, setActionBusy] = useState(false);
  const [diagnostic, setDiagnostic] = useState<PlatformDiagnostic | null>(null);
  const [confirmationDiagnostic, setConfirmationDiagnostic] = useState<PlatformDiagnostic | null>(null);
  const [notice, setNotice] = useState("");
  const [pending, setPending] = useState<PendingAction | null>(null);

  const refresh = async (selectedMembershipId: string | null = selected?.membershipId ?? null) => {
    setBusy(true);
    setDiagnostic(null);
    try {
      const administrators = await getPlatformAdministrators(token);
      setItems(administrators);
      if (selectedMembershipId) {
        const next = administrators.find((item) => item.membershipId === selectedMembershipId) ?? null;
        setSelected(next);
        setSessions(next ? await getPlatformAdministratorSessions(token, next.membershipId) : []);
      } else {
        setSelected(null);
        setSessions([]);
      }
    } catch (caught) {
      setDiagnostic(toPlatformDiagnostic(caught, language, "administrators"));
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    void refresh(null);
  }, [token]);

  const clearFeedback = () => {
    setDiagnostic(null);
    setConfirmationDiagnostic(null);
    setNotice("");
  };

  const openSessions = async (administrator: PlatformAdministratorSummary) => {
    clearFeedback();
    setSelected(administrator);
    setActionBusy(true);
    try {
      setSessions(await getPlatformAdministratorSessions(token, administrator.membershipId));
    } catch (caught) {
      setDiagnostic(toPlatformDiagnostic(caught, language, "sessions"));
    } finally {
      setActionBusy(false);
    }
  };

  const openConfirmation = (action: PendingAction) => {
    clearFeedback();
    setPending(action);
  };

  const runConfirmedAction = async (reason: string) => {
    if (!pending || actionBusy) return;

    setActionBusy(true);
    setConfirmationDiagnostic(null);
    setDiagnostic(null);
    setNotice("");
    let selectedMembershipId = selected?.membershipId ?? null;

    try {
      if (pending.kind === "grant") {
        await addPlatformAdministrator(
          token,
          pending.email,
          reason,
          createPlatformSecurityIdempotencyKey("platform-owner-add")
        );
        setEmail("");
        selectedMembershipId = null;
        setNotice(ar ? "تم منح صلاحية PlatformOwner بنجاح." : "PlatformOwner access was granted successfully.");
      } else if (pending.kind === "membership") {
        const response = await mutatePlatformAdministrator(
          token,
          pending.administrator.membershipId,
          pending.action,
          pending.administrator.version,
          reason,
          createPlatformSecurityIdempotencyKey(`platform-owner-${pending.action}`)
        );
        selectedMembershipId = selected?.membershipId === pending.administrator.membershipId
          ? pending.administrator.membershipId
          : selectedMembershipId;
        const actionLabel = pending.action === "suspend"
          ? ar ? "إيقاف العضوية" : "Membership suspended"
          : pending.action === "reactivate"
            ? ar ? "إعادة تفعيل العضوية" : "Membership reactivated"
            : ar ? "إلغاء العضوية" : "Membership revoked";
        setNotice(ar
          ? `تم ${actionLabel}. الجلسات الملغاة: ${response.revokedSessionCount}.`
          : `${actionLabel}. Revoked sessions: ${response.revokedSessionCount}.`);
      } else if (pending.kind === "session") {
        await revokePlatformSession(
          token,
          pending.session.sessionId,
          reason,
          createPlatformSecurityIdempotencyKey("platform-session-revoke")
        );
        selectedMembershipId = pending.administrator.membershipId;
        setNotice(ar
          ? "تمت معالجة إلغاء الجلسة، وسيُفرض القرار عند الطلب التالي."
          : "The session revocation was processed and will be enforced on the next request.");
      } else {
        const response = await revokeAllPlatformAdministratorSessions(
          token,
          pending.administrator.membershipId,
          reason,
          createPlatformSecurityIdempotencyKey("platform-sessions-revoke-all")
        );
        selectedMembershipId = pending.administrator.membershipId;
        setNotice(ar
          ? `تم إلغاء ${response.revokedSessionCount} جلسة نشطة.`
          : `${response.revokedSessionCount} active sessions were revoked.`);
      }

      setPending(null);
      window.dispatchEvent(new CustomEvent(auditRefreshEvent));
      await refresh(selectedMembershipId);
    } catch (caught) {
      const scope = pending.kind === "session" || pending.kind === "all-sessions"
        ? "sessions"
        : "administrators";
      setConfirmationDiagnostic(toPlatformDiagnostic(caught, language, scope));
    } finally {
      setActionBusy(false);
    }
  };

  const confirmation = pending ? getConfirmationCopy(pending, language) : null;

  return (
    <div className="platform-admin-security">
      {diagnostic ? <PlatformDiagnosticAlert diagnostic={diagnostic} language={language} /> : null}
      {notice ? (
        <div className="platform-admin-security__notice" role="status" aria-live="polite">
          {notice}
        </div>
      ) : null}

      <section className="platform-panel">
        <div className="platform-admin-security__header">
          <div>
            <span className="platform-kicker">PLATFORM IDENTITY</span>
            <h2>{ar ? "إدارة ملاك المنصة" : "PlatformOwner administration"}</h2>
            <p>{ar
              ? "صلاحيات Control Plane فقط. لا تمنح هذه العضوية أي صلاحية داخل مؤسسة أو فرع."
              : "Control-plane authority only. This membership grants no tenant or branch authority."}</p>
          </div>
          <Button
            variant="secondary"
            type="button"
            disabled={busy || actionBusy}
            onClick={() => {
              setNotice("");
              void refresh();
            }}
          >
            {ar ? "تحديث" : "Refresh"}
          </Button>
        </div>

        <div className="platform-admin-security__add">
          <Field
            label={ar ? "بريد حساب Leylion الحالي" : "Existing Leylion account email"}
            type="email"
            autoComplete="off"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            placeholder="name@example.com"
          />
          <Button
            type="button"
            disabled={actionBusy || !email.trim()}
            onClick={() => openConfirmation({ kind: "grant", email: email.trim() })}
          >
            {ar ? "منح PlatformOwner" : "Grant PlatformOwner"}
          </Button>
        </div>

        {busy ? (
          <div className="platform-loading">
            <Spinner label={ar ? "جارٍ تحميل ملاك المنصة" : "Loading PlatformOwners"} />
          </div>
        ) : (
          <div className="platform-table-wrap">
            <table className="platform-table">
              <thead>
                <tr>
                  <th>{ar ? "الحساب" : "Account"}</th>
                  <th>{ar ? "الحالة" : "Status"}</th>
                  <th>MFA</th>
                  <th>{ar ? "الجلسات" : "Sessions"}</th>
                  <th>{ar ? "الإجراءات" : "Actions"}</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 ? (
                  <tr><td colSpan={5}>{ar ? "لا توجد عضويات PlatformOwner." : "No PlatformOwner memberships are recorded."}</td></tr>
                ) : items.map((item) => (
                  <tr key={item.membershipId}>
                    <td>
                      <strong>{item.displayName}</strong>
                      <small><bdi dir="ltr">{item.email}</bdi></small>
                      <small><bdi dir="ltr">{item.membershipId}</bdi></small>
                    </td>
                    <td>
                      <StatusBadge tone={item.status === "Active" ? "success" : item.status === "Suspended" ? "warning" : "danger"}>
                        {item.status}
                      </StatusBadge>
                      <small>v{item.version}</small>
                    </td>
                    <td>
                      <StatusBadge tone={item.mfaConfigured ? "success" : "danger"}>
                        {item.mfaConfigured ? (ar ? "مفعّل" : "Configured") : (ar ? "غير مفعّل" : "Missing")}
                      </StatusBadge>
                    </td>
                    <td>
                      <button
                        className="platform-admin-security__link"
                        type="button"
                        disabled={actionBusy}
                        onClick={() => void openSessions(item)}
                      >
                        {item.activeSessionCount} {ar ? "نشطة" : "active"}
                      </button>
                    </td>
                    <td>
                      <div className="platform-admin-security__actions">
                        {item.status === "Active" ? (
                          <Button
                            variant="secondary"
                            type="button"
                            disabled={actionBusy}
                            onClick={() => openConfirmation({ kind: "membership", action: "suspend", administrator: item })}
                          >
                            {ar ? "إيقاف" : "Suspend"}
                          </Button>
                        ) : null}
                        {item.status !== "Active" ? (
                          <Button
                            variant="secondary"
                            type="button"
                            disabled={actionBusy}
                            onClick={() => openConfirmation({ kind: "membership", action: "reactivate", administrator: item })}
                          >
                            {ar ? "إعادة تفعيل" : "Reactivate"}
                          </Button>
                        ) : null}
                        {item.status !== "Revoked" ? (
                          <Button
                            variant="secondary"
                            type="button"
                            disabled={actionBusy}
                            onClick={() => openConfirmation({ kind: "membership", action: "revoke", administrator: item })}
                          >
                            {ar ? "إلغاء العضوية" : "Revoke membership"}
                          </Button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="platform-panel">
        <div className="platform-admin-security__header">
          <div>
            <h2>{ar ? "إدارة جلسات المنصة" : "Platform session administration"}</h2>
            <p>{ar
              ? "إلغاء الجلسة يصبح نافذًا عند الطلب التالي، وإعادة تفعيل العضوية لا تعيد جلسة ملغاة."
              : "Revocation takes effect on the next request; membership reactivation never resurrects a revoked session."}</p>
          </div>
        </div>

        {!selected ? (
          <p className="platform-admin-security__empty">
            {ar
              ? "اختر عدد الجلسات بجانب أحد ملاك المنصة لعرضها."
              : "Select the session count beside a PlatformOwner to inspect its sessions."}
          </p>
        ) : (
          <>
            <div className="platform-admin-security__selected">
              <div>
                <strong>{selected.displayName}</strong>
                <span><bdi dir="ltr">{selected.email}</bdi></span>
              </div>
              <Button
                variant="secondary"
                type="button"
                disabled={actionBusy || selected.activeSessionCount === 0}
                onClick={() => openConfirmation({ kind: "all-sessions", administrator: selected })}
              >
                {ar ? "إلغاء جميع الجلسات النشطة" : "Revoke all active sessions"}
              </Button>
            </div>

            <div className="platform-table-wrap">
              <table className="platform-table">
                <thead>
                  <tr>
                    <th>{ar ? "الجلسة" : "Session"}</th>
                    <th>{ar ? "الحالة" : "Status"}</th>
                    <th>{ar ? "آخر نشاط" : "Last seen"}</th>
                    <th>{ar ? "الانتهاء" : "Expires"}</th>
                    <th>{ar ? "الإجراء" : "Action"}</th>
                  </tr>
                </thead>
                <tbody>
                  {sessions.length === 0 ? (
                    <tr><td colSpan={5}>{ar ? "لا توجد جلسات مسجلة." : "No platform sessions are recorded."}</td></tr>
                  ) : sessions.map((session) => (
                    <tr key={session.sessionId}>
                      <td>
                        <strong>{session.authenticationMethod}</strong>
                        <small><bdi dir="ltr">{session.sessionId}</bdi></small>
                      </td>
                      <td>
                        <StatusBadge tone={session.status === "Active" ? "success" : "neutral"}>
                          {session.status}
                        </StatusBadge>
                        {session.revocationReason ? <small>{session.revocationReason}</small> : null}
                      </td>
                      <td>
                        <time dateTime={session.lastSeenAt} title={new Date(session.lastSeenAt).toISOString()}>
                          {formatTimestamp(session.lastSeenAt, language)}
                        </time>
                      </td>
                      <td>
                        <time dateTime={session.expiresAt} title={new Date(session.expiresAt).toISOString()}>
                          {formatTimestamp(session.expiresAt, language)}
                        </time>
                      </td>
                      <td>
                        {session.status === "Active" ? (
                          <Button
                            variant="secondary"
                            type="button"
                            disabled={actionBusy}
                            onClick={() => openConfirmation({ kind: "session", administrator: selected, session })}
                          >
                            {ar ? "إلغاء الجلسة" : "Revoke session"}
                          </Button>
                        ) : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </section>

      {confirmation && pending ? (
        <PlatformConfirmationDialog
          open
          language={language}
          title={confirmation.title}
          description={confirmation.description}
          target={confirmation.target}
          action={confirmation.action}
          impact={confirmation.impact}
          sessionImpact={confirmation.sessionImpact}
          confirmLabel={confirmation.confirmLabel}
          destructive={confirmation.destructive}
          busy={actionBusy}
          diagnostic={confirmationDiagnostic}
          onConfirm={(reason) => void runConfirmedAction(reason)}
          onClose={() => {
            if (!actionBusy) {
              setPending(null);
              setConfirmationDiagnostic(null);
            }
          }}
        />
      ) : null}
    </div>
  );
}

function getConfirmationCopy(action: PendingAction, language: PlatformLanguage) {
  const ar = language === "ar";
  if (action.kind === "grant") {
    return {
      title: ar ? "تأكيد منح PlatformOwner" : "Confirm PlatformOwner grant",
      description: ar ? "راجع الحساب والأثر ثم سجّل سبب المنح." : "Review the account and impact, then record the grant reason.",
      target: action.email,
      action: ar ? "منح صلاحية PlatformOwner" : "Grant PlatformOwner access",
      impact: ar ? "يمنح صلاحية Control Plane فقط ولا يمنح أي صلاحية داخل مؤسسة أو فرع." : "Grants control-plane authority only; no tenant or branch authority is granted.",
      sessionImpact: ar ? "لا يتم إنشاء جلسة تلقائيًا. يجب على المستخدم تسجيل الدخول واختيار سياق المنصة." : "No session is created automatically. The user must sign in and select the platform context.",
      confirmLabel: ar ? "تأكيد المنح" : "Confirm grant",
      destructive: false
    };
  }

  if (action.kind === "membership") {
    const administrator = action.administrator;
    const target = `${administrator.displayName} · ${administrator.email} · ${administrator.membershipId}`;
    if (action.action === "suspend") {
      return {
        title: ar ? "تأكيد إيقاف PlatformOwner" : "Confirm PlatformOwner suspension",
        description: ar ? "هذا إجراء أمني حساس ويتطلب سببًا واضحًا." : "This is a sensitive security action and requires a clear reason.",
        target,
        action: ar ? "إيقاف العضوية" : "Suspend membership",
        impact: ar ? "يمنع العضو من استخدام صلاحية PlatformOwner حتى إعادة التفعيل." : "Blocks PlatformOwner authority until the membership is reactivated.",
        sessionImpact: ar ? `سيتم إلغاء جميع الجلسات النشطة الحالية (${administrator.activeSessionCount}).` : `All current active sessions will be revoked (${administrator.activeSessionCount}).`,
        confirmLabel: ar ? "تأكيد الإيقاف" : "Confirm suspension",
        destructive: true
      };
    }
    if (action.action === "reactivate") {
      return {
        title: ar ? "تأكيد إعادة تفعيل PlatformOwner" : "Confirm PlatformOwner reactivation",
        description: ar ? "تحقق من استمرار أهلية الحساب وMFA قبل المتابعة." : "Verify that the account remains eligible and MFA-enabled before continuing.",
        target,
        action: ar ? "إعادة تفعيل العضوية" : "Reactivate membership",
        impact: ar ? "يعيد صلاحية PlatformOwner بعد تحقق الخادم من الحساب وMFA." : "Restores PlatformOwner authority after server-side account and MFA validation.",
        sessionImpact: ar ? "لن تعود أي جلسة ملغاة للحياة؛ يلزم تسجيل دخول جديد." : "No revoked session is resurrected; a new sign-in is required.",
        confirmLabel: ar ? "تأكيد إعادة التفعيل" : "Confirm reactivation",
        destructive: false
      };
    }
    return {
      title: ar ? "تأكيد إلغاء عضوية PlatformOwner" : "Confirm PlatformOwner membership revocation",
      description: ar ? "راجع أثر الإلغاء الدائم قبل المتابعة." : "Review the persistent revocation impact before continuing.",
      target,
      action: ar ? "إلغاء العضوية" : "Revoke membership",
      impact: ar ? "ينقل العضوية إلى Revoked مع بقاء قواعد دورة العضوية الخادمية نافذة." : "Moves the membership to Revoked while preserving the server-side lifecycle rules.",
      sessionImpact: ar ? `سيتم إلغاء جميع الجلسات النشطة الحالية (${administrator.activeSessionCount}).` : `All current active sessions will be revoked (${administrator.activeSessionCount}).`,
      confirmLabel: ar ? "تأكيد إلغاء العضوية" : "Confirm membership revocation",
      destructive: true
    };
  }

  if (action.kind === "session") {
    return {
      title: ar ? "تأكيد إلغاء جلسة منصة" : "Confirm platform-session revocation",
      description: ar ? "سيصبح الإلغاء نافذًا عند الطلب التالي من هذه الجلسة." : "Revocation becomes effective on this session's next request.",
      target: `${action.administrator.email} · ${action.session.sessionId}`,
      action: ar ? "إلغاء جلسة واحدة" : "Revoke one session",
      impact: ar ? "تبقى العضوية نفسها كما هي؛ يتأثر رمز الجلسة المحدد فقط." : "The membership remains unchanged; only the selected session token is affected.",
      sessionImpact: ar ? "لن تعود الجلسة الملغاة للحياة بعد إعادة تفعيل العضوية." : "The revoked session will never be resurrected by membership reactivation.",
      confirmLabel: ar ? "تأكيد إلغاء الجلسة" : "Confirm session revocation",
      destructive: true
    };
  }

  return {
    title: ar ? "تأكيد إلغاء جميع جلسات المنصة" : "Confirm revocation of all platform sessions",
    description: ar ? "سيتم إلغاء كل جلسة نشطة مرتبطة بهذه العضوية." : "Every active session linked to this membership will be revoked.",
    target: `${action.administrator.displayName} · ${action.administrator.email} · ${action.administrator.membershipId}`,
    action: ar ? "إلغاء جميع الجلسات النشطة" : "Revoke all active sessions",
    impact: ar ? "تبقى العضوية فعّالة ما لم يتم تغيير حالتها بإجراء مستقل." : "The membership remains active unless its lifecycle is changed separately.",
    sessionImpact: ar ? `عدد الجلسات النشطة المعروض حاليًا: ${action.administrator.activeSessionCount}.` : `Currently displayed active-session count: ${action.administrator.activeSessionCount}.`,
    confirmLabel: ar ? "تأكيد إلغاء جميع الجلسات" : "Confirm revocation of all sessions",
    destructive: true
  };
}
